import React, { useState, useRef, useEffect } from "react";
import { UiComponents, UnoStoreHooks } from "@simulanisindia/ui";
import { CheckIcon, CloseIcon, EditIcon, UploadIcon } from "../../Assest/icons/icons";
import UploadMediaModal from "../modals/UploadMediaModal";
import "react-quill/dist/quill.snow.css";
import apiClient from "../../apiClient";
import { FETCH_MODULE_TAG_LIST, MODULE_BASIC_DETAILS, UPDATE_MODULE_BASIC_DETAILS } from "../../apiClient/apiEndpoints";
import { QuillTextField } from "../../utils/QuillTextField";
import uploads from "../../utils/uploads";

const { uploadFile } = uploads;
const { SimInputBar, DropdownSearch, SimToggleSwitch } = UiComponents;
const { useUnoSelector } = UnoStoreHooks;

const ModuleBasicDetails = ({ id }) => {
  const configData = useUnoSelector((state) => state.config);
  const UNO_URL = configData.config.UNO_URL;

  const [moduleDetail, setModuleDetail] = useState({});
  const [uploadedMediaUUIDs, setUploadedMediaUUIDs] = useState([]);
  const [uploadedMedia, setUploadedMedia] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [difficultyTags, setDifficultyTags] = useState([]);

  const [courseName, setCourseName] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(351);
  const [level, setLevel] = useState({});
  const [courseDuration, setCourseDuration] = useState(1);

  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [thumbnailUUID, setThumbnailUUID] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [learningItems, setLearningItems] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [newLearningItem, setNewLearningItem] = useState("");
  const [newOutcome, setNewOutcome] = useState("");
  const [editingLearningItem, setEditingLearningItem] = useState(null);
  const [editingOutcome, setEditingOutcome] = useState(null);
  const [editLearningText, setEditLearningText] = useState("");
  const [editOutcomeText, setEditOutcomeText] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [isPublish, setIsPublished] = useState(false);

  const handleUploadAndPreview = async (file) => {
    try {
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setThumbnailPreview(e.target.result);
      reader.readAsDataURL(file);

      const response = await uploadFile(UNO_URL, id, file);
      if (response?.data?.url) {
        setThumbnailUUID(response.data.url);
      } else {
        console.error("Failed to get UUID from upload response:", response);
        console.error("Failed to get UUID from upload response:", response);
      }
    } catch (error) {
      console.error("Error uploading thumbnail:", error);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await handleUploadAndPreview(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = async (e) => {
    if (e.target.files && e.target.files[0]) {
      await handleUploadAndPreview(e.target.files[0]);
    }
  };

  const openUploadModal = () => setShowUploadModal(true);
  const closeUploadModal = () => setShowUploadModal(false);
  const handleMediaUpload = (urls) => setUploadedMediaUUIDs((prev) => [...prev, ...urls]);

  const addLearningItem = () => {
    if (newLearningItem.trim()) {
      setLearningItems([...learningItems, newLearningItem.trim()]);
      setNewLearningItem("");
    }
  };
  const removeLearningItem = (index) => setLearningItems(learningItems.filter((_, i) => i !== index));
  const addOutcome = () => {
    if (newOutcome.trim()) {
      setOutcomes([...outcomes, newOutcome.trim()]);
      setNewOutcome("");
    }
  };
  const removeOutcome = (index) => setOutcomes(outcomes.filter((_, i) => i !== index));

  const startEditLearningItem = (index) => {
    setEditingLearningItem(index);
    setEditLearningText(learningItems[index]);
  };
  const saveEditLearningItem = () => {
    if (editLearningText.trim()) {
      const updated = [...learningItems];
      updated[editingLearningItem] = editLearningText.trim();
      setLearningItems(updated);
    }
    setEditingLearningItem(null);
    setEditLearningText("");
  };
  const cancelEditLearningItem = () => {
    setEditingLearningItem(null);
    setEditLearningText("");
  };

  const startEditOutcome = (index) => {
    setEditingOutcome(index);
    setEditOutcomeText(outcomes[index]);
  };
  const saveEditOutcome = () => {
    if (editOutcomeText.trim()) {
      const updated = [...outcomes];
      updated[editingOutcome] = editOutcomeText.trim();
      setOutcomes(updated);
    }
    setEditingOutcome(null);
    setEditOutcomeText("");
  };
  const cancelEditOutcome = () => {
    setEditingOutcome(null);
    setEditOutcomeText("");
  };

  const handleDifficultyTag = (e) => {
    setLevel(e);
  };

  const fetchModuleDetails = async () => {
    try {
      const details = await apiClient.get(`${UNO_URL}${MODULE_BASIC_DETAILS}/${id}`);
      setModuleDetail(details);
      // setIsPublished(details?.publish);
      setCourseName(details?.name || "");
      setShortDescription(details?.versions[0]?.data?.short || "");
      setDescription(details?.versions[0]?.data?.description || "");
      setCourseDuration(details?.versions[0]?.data?.courseDuration || 7);
      setLearningItems(details?.versions[0]?.data?.learningPoints || []);
      setOutcomes(details?.versions[0]?.data?.outcomesPoints || []);
      setThumbnailPreview(details?.versions[0]?.data?.thumbnail || null);
      const mediaUrls = details?.versions[0]?.data?.media?.map((e) => e.url) || [];
      setUploadedMedia(mediaUrls);

      const moduleTags =
        details?.tags
          ?.filter((item) => item.categoryId == 351)
          ?.map((item) => ({ label: item.name, value: item.id })) || [];
      setSelectedTags(moduleTags);
      const moduleLanguages =
        details?.tags?.filter((item) => item.categoryId == 5)?.map((item) => ({ label: item.name, value: item.id })) ||
        [];
      setSelectedLanguages(moduleLanguages);
      const difficultyTags =
        details?.tags?.filter((item) => item.categoryId == 6)?.map((item) => ({ label: item.name, value: item.id })) ||
        [];
      setLevel(difficultyTags[0]);
    } catch (error) {
      console.error("Error fetching module details:", error.message);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await apiClient.get(`${UNO_URL}${FETCH_MODULE_TAG_LIST}/351`);
      const tagOptions = res?.tags?.map((item) => ({ label: item.name, value: item.id })) || [];
      setTags(tagOptions);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchLanguageOptions = async () => {
    try {
      const res = await apiClient.get(`${UNO_URL}${FETCH_MODULE_TAG_LIST}/5`);
      const options = res?.tags?.map((item) => ({ label: item.name, value: item.id })) || [];
      setAvailableLanguages(options);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchDefiicultyLevelTags = async () => {
    try {
      const res = await apiClient.get(`${UNO_URL}${FETCH_MODULE_TAG_LIST}/6`);
      const options = res?.tags?.map((item) => ({ label: item.name, value: item.id })) || [];
      setDifficultyTags(options);
    } catch (error) {
      console.error(error);
    }
  };

  const validateFields = () => {
    const newErrors = {};
    if (!courseName.trim()) newErrors.courseName = "Course Name is required";
    if (!shortDescription.trim()) newErrors.shortDescription = "Short Description is required";
    // if (!description.trim()) newErrors.description = "Description is required";
    if (!selectedTags.length) newErrors.tags = "Select at least one tag";
    if (!thumbnailUUID && !thumbnailPreview) newErrors.thumbnail = "Thumbnail is required";
    if (!level) newErrors.level = "Select level";
    if (!selectedLanguages.length) newErrors.languages = "Select at least one language";
    if (!courseDuration || courseDuration <= 0) newErrors.courseDuration = "Enter valid course duration";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateModuleBasicDetail = async () => {
    if (!validateFields()) return;
    setSaving(() => true);
    try {
      const media = uploadedMediaUUIDs?.length
        ? uploadedMediaUUIDs.map((item) => ({ url: item })).filter((e) => e)
        : uploadedMedia.map((item) => ({ url: item }));
      const body = {
        name: courseName,
        tags: [...selectedTags.map((tag) => tag.value), ...selectedLanguages?.map((tag) => tag.value), level?.value],
        tagCategory: selectedCategory ? [selectedCategory] : [],
        applicationsIds: selectedApplications.map((app) => app.value),
        applicationsIds: selectedApplications.map((app) => app.value),
        data: {
          thumbnail: thumbnailUUID || thumbnailPreview,
          media: media,
          short: shortDescription,
          description,
          courseDuration: courseDuration.toString(),
          learningPoints: learningItems,
          outcomesPoints: outcomes,
        },
        active: false,
        publish: isPublish,
      };

      await apiClient.post(`${UNO_URL}${UPDATE_MODULE_BASIC_DETAILS}/${id}`, body);
      setSaving(() => false);
    } catch (error) {
      console.error("Error updating module:", error.message);
    }
  };

  useEffect(() => {
    fetchModuleDetails();
    fetchTags();
    fetchLanguageOptions();
    fetchDefiicultyLevelTags();
  }, [id]);

  return (
    <>
      <div className="save_absolute modalFooterB">
        <button className="actionButton save" onClick={updateModuleBasicDetail}>
          {saving ? "Updating..." : "Update"}
        </button>
      </div>

      <div className="module-basic-details">
        <div className="row m-0">
          {/* Thumbnail Upload */}
          <div className="col-md-4">
            <div className="thumbnail-upload-container">
              <div className="media-side">
                {thumbnailPreview ? (
                  <div className="thumbnail-preview">
                    <img src={thumbnailPreview} alt="Thumbnail preview" />
                    <button
                      type="button"
                      className="remove-thumbnail"
                      onClick={() => {
                        setThumbnailFile(null);
                        setThumbnailPreview(null);
                        setThumbnailUUID(null);
                      }}
                    >
                      <CloseIcon />
                    </button>
                  </div>
                ) : (
                  <div
                    className={`thumbnail-upload ${dragActive ? "drag-active" : ""}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileInput}
                      accept="image/*"
                      style={{ display: "none" }}
                    />
                    <UploadIcon />
                    <p>Drag & drop or choose file to upload</p>
                  </div>
                )}
              </div>
              <div className="content-side">
                <p className="title">
                  Thumbnail Image <span>*</span>
                </p>
                <span className="redInfo">
                  Required Resolution* (1280px x 720px)
                  <br />
                  Max file size: 100KB
                </span>
                {!thumbnailPreview && (
                  <button type="button" className="actionButton" onClick={() => fileInputRef.current.click()}>
                    Choose File
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Media Gallery */}
          <div className="col-md-4">
            <div className="thumbnail-upload-container media-gallery">
              <div className="media-side">
                <div className="thumbnail-upload" onClick={openUploadModal}>
                  <UploadIcon />
                  <p>Choose files to upload</p>
                </div>
              </div>
              <div className="content-side">
                <p className="title">Media Gallery</p>
              </div>
            </div>
          </div>

          {/* Level & Language */}
          <div className="col-md-4">
            <div className="row">
              {/* Published */}
              <div className="col-12">
                <div className="row">
                  <div className="col-7">
                    <div className="inputItem">
                      <p>
                        Select Level<span>*</span>
                      </p>
                      <div className="level-buttons">
                        {difficultyTags?.map((item) => {
                          return (
                            <button
                              type="button"
                              className={`btn ${level?.value == item.value ? "btn-danger" : "btn-outline-danger"}`}
                              onClick={() => handleDifficultyTag(item)}
                            >
                              {item.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  {/* <div className="col-5">
                    <div className="inputItem">
                      <p>Publish</p>
                      <div className="level-buttons">
                        <SimToggleSwitch checked={isPublish} onClick={() => setIsPublished((prev) => !prev)} />
                      </div>
                    </div>
                  </div> */}
                </div>
              </div>
              {/* Tags */}
              <div className="col-12"></div>
              <div className="col-12 mt-3">
                <div className="inputItem">
                  <p>
                    Language <span>*</span>
                  </p>
                  <DropdownSearch
                    label=""
                    error={errors.languages}
                    selectProps={{
                      options: availableLanguages,
                      value: selectedLanguages,
                      isMulti: true,
                      placeholder: "Select languages...",
                      onChange: (options) => setSelectedLanguages(options || []),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Course Name */}
          <div className="col-md-4 mt-3">
            <div className="inputItem">
              <p>
                Course Name <span>*</span>
              </p>
              <SimInputBar
                errorMessage={errors.courseName}
                placeholder="Course Name"
                value={courseName}
                onChange={(e) => setCourseName(e.target.value)}
              />
            </div>
          </div>

          {/* Short Description */}
          <div className="col-md-4 mt-3">
            <div className="inputItem">
              <p>
                Short Description <span>*</span>
              </p>
              <SimInputBar
                errorMessage={errors.shortDescription}
                placeholder="Short Description"
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
              />
            </div>
          </div>

          {/* Course Duration */}
          <div className="col-md-4 mt-3">
            <div className="inputItem">
              <p>
                Course Duration <span>*</span>
              </p>
              <SimInputBar
                errorMessage={errors.courseDuration}
                type="number"
                placeholder="Duration in days"
                value={courseDuration}
                onChange={(e) => setCourseDuration(e.target.value)}
              />
            </div>
          </div>

          {/* Tags & Description */}
          <div className="col-md-4 mt-3">
            <div className="row">
              <div className="col-12">
                <div className="inputItem">
                  <p>
                    Tags <span>*</span>
                  </p>
                  <DropdownSearch
                    label=""
                    error={errors.tags}
                    selectProps={{
                      options: tags,
                      value: selectedTags,
                      isMulti: true,
                      placeholder: "Select Tags...",
                      onChange: (options) => setSelectedTags(options || []),
                    }}
                  />
                </div>
              </div>
              <div className="col-12 mt-3">
                <div className="inputItem">
                  <p>Description</p>
                  <div className="rich-text-editor">
                    <QuillTextField value={description} onChange={setDescription} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Learning Items */}
          <div className="col-md-4 mt-3">
            <div className="inputItem">
              <p>What you will learn ?</p>
              <div className="learning-items-container">
                {learningItems.map((item, index) => (
                  <div key={index} className="learning-item">
                    {editingLearningItem === index ? (
                      <div className="edit-item-section">
                        <SimInputBar
                          value={editLearningText}
                          onChange={(e) => setEditLearningText(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") saveEditLearningItem();
                            if (e.key === "Escape") cancelEditLearningItem();
                          }}
                          autoFocus
                        />
                        <div className="edit-buttons">
                          <button type="button" className="save-btn" onClick={saveEditLearningItem}>
                            <CheckIcon />
                          </button>
                          <button type="button" className="cancel-btn" onClick={cancelEditLearningItem}>
                            <CloseIcon />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="item-text" onClick={() => startEditLearningItem(index)}>
                          {item}
                        </span>
                        <div className="item-actions">
                          <button
                            type="button"
                            className="edit-item-btn"
                            onClick={() => startEditLearningItem(index)}
                            title="Edit item"
                          >
                            <EditIcon />
                          </button>
                          <button
                            type="button"
                            className="remove-item-btn"
                            onClick={() => removeLearningItem(index)}
                            title="Remove item"
                          >
                            <CloseIcon />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
                <div className="add-item-section">
                  <SimInputBar
                    placeholder="Enter what you will learn..."
                    value={newLearningItem}
                    onChange={(e) => setNewLearningItem(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && addLearningItem()}
                  />
                  <button type="button" className="add-item-btn" onClick={addLearningItem}>
                    + Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Outcomes */}
          <div className="col-md-4 mt-3">
            <div className="inputItem">
              <p>Outcomes :</p>
              <div className="learning-items-container">
                {outcomes.map((item, index) => (
                  <div key={index} className="learning-item">
                    {editingOutcome === index ? (
                      <div className="edit-item-section">
                        <SimInputBar
                          value={editOutcomeText}
                          onChange={(e) => setEditOutcomeText(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") saveEditOutcome();
                            if (e.key === "Escape") cancelEditOutcome();
                          }}
                          autoFocus
                        />
                        <div className="edit-buttons">
                          <button type="button" className="save-btn" onClick={saveEditOutcome}>
                            ✓
                          </button>
                          <button type="button" className="cancel-btn" onClick={cancelEditOutcome}>
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="item-text" onClick={() => startEditOutcome(index)}>
                          {item}
                        </span>
                        <div className="item-actions">
                          <button
                            type="button"
                            className="edit-item-btn"
                            onClick={() => startEditOutcome(index)}
                            title="Edit item"
                          >
                            <EditIcon />
                          </button>
                          <button
                            type="button"
                            className="remove-item-btn"
                            onClick={() => removeOutcome(index)}
                            title="Remove item"
                          >
                            <CloseIcon />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
                <div className="add-item-section">
                  <SimInputBar
                    placeholder="Enter expected outcome..."
                    value={newOutcome}
                    onChange={(e) => setNewOutcome(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && addOutcome()}
                  />
                  <button type="button" className="add-item-btn" onClick={addOutcome}>
                    + Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <UploadMediaModal
          show={showUploadModal}
          handleClose={closeUploadModal}
          UNO_URL={UNO_URL}
          moduleUUID={id}
          onMediaUpload={handleMediaUpload}
          uploadedMedia={uploadedMedia}
        />
      </div>
    </>
  );
};

export default ModuleBasicDetails;
